import React from "react";
import ReactModal from "react-modal";

const show = (v) => {
    if (v === null || v === undefined) return "N/A";
    const s = String(v).trim();
    return s || "N/A";
};

const fmtDate = (dateString) => {
    if (!dateString) return "N/A";
    const dt = new Date(String(dateString).replace(/ GMT$/, ""));
    if (Number.isNaN(dt.getTime())) return "N/A";
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = dt.toLocaleString("en-AU", { month: "short" });
    const yr = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${day} ${mon}, ${yr} ${hh}:${mm}`;
};

const toUrl = (value) => {
    const s = String(value || "").trim();
    if (!s) return "";
    return s.startsWith("http://") || s.startsWith("https://") ? s : `https://${s}`;
};

export default function LinkedContextDialog({
    isOpen,
    onClose,
    title = "Linked Context",
    loading = false,
    record = null,
    contacts = [],
    onAddBlacklist,
    blacklistBusy = false,
}) {
    const canAddBlacklist = !!(record && record.domain && onAddBlacklist);

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(88rem,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto outline-none border border-slate-200"
            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3 z-[200]"
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">{title}</h2>
                    <p className="text-sm text-slate-600 mt-1">Company details and linked contacts.</p>
                </div>
                <button
                    onClick={onClose}
                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                    aria-label="Close"
                    type="button"
                >
                    ×
                </button>
            </div>

            {loading ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">Loading...</div>
            ) : (
                <div className="space-y-4">
                    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</div>
                                <div className="mt-1 text-base font-bold text-slate-900">{show(record?.company_name)}</div>
                            <div className="mt-1 text-sm">
                                <span className="text-slate-500">Domain: </span>
                                {record?.domain ? (
                                    <a
                                        href={toUrl(record.domain)}
                                        target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-900 hover:decoration-slate-900"
                                        >
                                            {show(record.domain)}
                                        </a>
                                    ) : (
                                        <span className="text-slate-700">N/A</span>
                                    )}
                                </div>
                                <div className="mt-1 text-sm">
                                    <span className="text-slate-500">LinkedIn: </span>
                                    {record?.linkedin || record?.company_linkedin ? (
                                        <a
                                            href={toUrl(record?.linkedin || record?.company_linkedin)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-900 hover:decoration-slate-900"
                                        >
                                            {show(record?.linkedin || record?.company_linkedin)}
                                        </a>
                                    ) : (
                                        <span className="text-slate-700">N/A</span>
                                    )}
                                </div>
                            </div>

                            {canAddBlacklist && (
                                <button
                                    type="button"
                                    onClick={() => onAddBlacklist(record.domain)}
                                    disabled={blacklistBusy}
                                    className="h-10 rounded-xl bg-red-700 px-4 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Add To Blacklist
                                </button>
                            )}
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                            <div><span className="text-slate-500">Category:</span> <span className="font-semibold text-slate-800">{show(record?.business_category)}</span></div>
                            <div><span className="text-slate-500">Country:</span> <span className="font-semibold text-slate-800">{show(record?.country)}</span></div>
                            <div><span className="text-slate-500">Relevance:</span> <span className="font-semibold text-slate-800">{show(record?.relevance)}</span></div>
                            <div><span className="text-slate-500">Leverage:</span> <span className="font-semibold text-slate-800">{show(record?.leverage_score)}</span></div>
                            <div><span className="text-slate-500">Outreach:</span> <span className="font-semibold text-slate-800">{show(record?.outreach_score)}</span></div>
                            <div><span className="text-slate-500">Risk:</span> <span className="font-semibold text-slate-800">{show(record?.missed_opportunity_risk)}</span></div>
                            <div><span className="text-slate-500">Created:</span> <span className="font-semibold text-slate-800">{fmtDate(record?.created_at)}</span></div>
                            <div><span className="text-slate-500">Expanded:</span> <span className="font-semibold text-slate-800">{fmtDate(record?.expanded_at)}</span></div>
                            <div className="sm:col-span-2 lg:col-span-4">
                                <span className="text-slate-500">Summary:</span>{" "}
                                <span className="font-semibold text-slate-800 whitespace-pre-wrap break-words">{show(record?.summary)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-xl border border-slate-200 bg-white">
                        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Linked Contacts</div>
                            <div className="text-sm font-semibold text-slate-700">{contacts.length}</div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Name</th>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">AI Position</th>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Profile URL</th>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">LinkedIn Email</th>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Business Email</th>
                                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Phone</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((c) => (
                                        <tr key={c.contact_id} className="border-t border-slate-200">
                                            <td className="px-4 py-2 text-slate-900">{show(c.contact_name)}</td>
                                            <td className="px-4 py-2 text-slate-700">{show(c.ai_position)}</td>
                                            <td className="px-4 py-2 text-slate-700">
                                                {c.profile_url ? (
                                                    <a
                                                        href={toUrl(c.profile_url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block max-w-[260px] truncate text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                        title={String(c.profile_url)}
                                                    >
                                                        {show(c.profile_url)}
                                                    </a>
                                                ) : (
                                                    "N/A"
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-slate-700">{show(c.email)}</td>
                                            <td className="px-4 py-2 text-slate-700">{show(c.business_email)}</td>
                                            <td className="px-4 py-2 text-slate-700">{show(c.phone)}</td>
                                        </tr>
                                    ))}
                                    {!contacts.length && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-6 text-center text-slate-500">No linked contacts.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
        </ReactModal>
    );
}
